import React, { useCallback, useEffect, useMemo, useRef} from 'react';
import TsGrid from '../../components/tsGrid';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import ModalDialog from '../../components/ModalDialog';
import { parseDuration } from './ReleaseComparison';
import { formatValue } from '../Performance Dashboard/MetricCard';
import { Checkbox } from 'antd';

interface FullDetailsModalProps {
    onClose: () => void;
    releaseItem: ReleaseItem | null; 
    selectedOptions: {
        firstPass: boolean;
        rework: boolean;
    };

}

interface ExecutionTime {
    category: string; 
    first_pass: string; 
    rework: string;
}

interface WorkItem {
    id: number;
    type: string;
    created_date: string;
    title: string;
    project: string;
    execution_time: ExecutionTime[];
}

interface ReleaseItem {
    date: any;
    release: string;
    work_items: WorkItem[];
}

const FullDetailsModal: React.FC<FullDetailsModalProps> = ({
    onClose,
    releaseItem,
    selectedOptions
}) => {
 
    const gridRef = useRef<AgGridReact<any> | null>(null);
    const gridApiRef = useRef<any>(null);
    const containerStyle = useMemo(() => ({ width: '100%', height: '100%', marginBottom: '10px' }), []);
    const gridStyle = useMemo(() => ({ height: '90%', width: '100%', padding: "0px !important"}), []);


    const customComparator = (field: string) => {
        return (valueA: string | number, valueB: string | number) => {
            if (valueA === null || valueA === undefined) return -1;
            if (valueB === null || valueB === undefined) return 1;
    
            if (typeof valueA === "number" && typeof valueB === "number") {
                return valueA - valueB;
            }
    
            if (!isNaN(Number(valueA)) && !isNaN(Number(valueB))) {
                return parseFloat(valueA as string) - parseFloat(valueB as string);
            }
    
            if (isDate(valueA) && isDate(valueB)) {
                return new Date(valueA as string).getTime() - new Date(valueB as string).getTime();
            }
    
            return String(valueA).localeCompare(String(valueB));
        };
    };
    
    const isDate = (value: string | number) => {
        return typeof value === "string" && !isNaN(Date.parse(value));
    };
    
    const columnDefs: ColDef[] = useMemo(() => {
        if (!releaseItem || !releaseItem.work_items) return [];
      
        const baseColumns: ColDef[] = Object.keys(releaseItem.work_items[0]).flatMap((key) => {
          if (key === "execution_time") {
            const uniqueCategories = Array.from(
              new Set(
                releaseItem.work_items.flatMap((item) =>
                  item.execution_time
                    .map((time) => time.category)
                    .filter((category): category is string => Boolean(category))
                )
              )
            );
      
            return uniqueCategories.map((category): ColDef => ({
              field: category.toLowerCase(),
              headerName: category,
              tooltipField: category,
              headerTooltip: category,
              filter: "agMultiColumnFilter",
              menuTabs: ["filterMenuTab", "generalMenuTab"],
              editable: false,
              sortable: true,
              width: 100,
              cellStyle: { textAlign: "left", whiteSpace: "nowrap" },
              cellClass: "border-right",
              comparator: customComparator(category.toLowerCase()),
            }));
          } else if (key === "title") {
            return [];
          } else {
            return {
              field: key,
              headerName: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
              tooltipField: key,
              headerTooltip: key,
              filter: "agMultiColumnFilter",
              menuTabs: ["filterMenuTab", "generalMenuTab"],
              editable: false,
              sortable: true,
              width: 180,
              show: true,
              cellStyle: { textAlign: "left" },
              cellClass: "border-right",
              comparator: customComparator(key),
            } as ColDef;
          }
        });
      
        const leadTimeColumn: ColDef = {
          field: "lead_time",
          headerName: "Lead Time",
          tooltipField: "lead_time",
          headerTooltip: "Lead Time",
          filter: "agMultiColumnFilter",
          menuTabs: ["filterMenuTab", "generalMenuTab"],
          editable: false,
          sortable: true,
          width: 100,
          cellStyle: { textAlign: "left" },
          cellClass: "border-right",
          comparator: customComparator("lead_time"),
        };
      
        const reworkCheckboxColumn: ColDef = {
          field: "isRework",
          headerName: "Rework",
          headerTooltip: "Checked if not First Time Right",
          filter: false,
          sortable: false,
          editable: false,
          width: 100,
          cellStyle: { textAlign: "center" },
          cellClass: "border-right",
          cellRenderer: (params: any) => <Checkbox checked={!!params.value} />,
        };
      
        const updatedColumnDefs: ColDef[] = [
          ...baseColumns.slice(0, 3),
          leadTimeColumn,
          ...baseColumns.slice(3),
          reworkCheckboxColumn,
        ];
      
        const titleColumn: ColDef = {
          field: "title",
          headerName: "Title",
          tooltipField: "title",
          headerTooltip: "Title",
          filter: "agMultiColumnFilter",
          menuTabs: ["filterMenuTab", "generalMenuTab"],
          editable: false,
          sortable: true,
          minWidth: 500,
          flex: 1,
          cellStyle: { textAlign: "left", whiteSpace: 'normal', wordBreak: 'break-word' },
          cellClass: "border-right",
          autoHeight: true,
          comparator: customComparator("title"),

        };
      
        updatedColumnDefs.push(titleColumn);
      
        return updatedColumnDefs;
      }, [releaseItem]);

    const rowData = useMemo(() => {
        if (!releaseItem || !releaseItem.work_items) return [];

        return releaseItem.work_items.map((item) => {
            const executionTimeData = item.execution_time.reduce((acc, time) => {
                let value = 0;
    
                if (selectedOptions.firstPass && !selectedOptions.rework) {
                    const firstPassValue = parseDuration(time.first_pass).totalDays;
                    if (isNaN(firstPassValue)) {
                        console.error(`Invalid first_pass value: ${time.first_pass} for category: ${time.category}`);
                    } else {
                        value = firstPassValue;
                    }
                } else if (selectedOptions.rework && !selectedOptions.firstPass) {
                    const reworkValue = parseDuration(time.rework).totalDays;
                    if (isNaN(reworkValue)) {
                        console.error(`Invalid rework value: ${time.rework} for category: ${time.category}`);
                    } else {
                        value = reworkValue;
                    }
                } else if (selectedOptions.firstPass && selectedOptions.rework) {
                    const firstPassValue = parseDuration(time.first_pass).totalDays || 0;
                    const reworkValue = parseDuration(time.rework).totalDays;
                    value = firstPassValue + reworkValue;
                }
    
                if (time.category) {
                    acc[time.category.toLowerCase()] = formatValue(value);
                }
                return acc;
            }, {} as Record<string, string>);
    
            const leadTime = item.execution_time.reduce((acc, time) => {
                let value = 0;
    
                if (selectedOptions.firstPass && !selectedOptions.rework) {
                    value =  parseFloat((parseDuration(time.first_pass).totalDays || 0).toFixed(1));
                } else if (selectedOptions.rework && !selectedOptions.firstPass) {
                    value = parseFloat((parseDuration(time.rework).totalDays || 0).toFixed(1));
                } else if (selectedOptions.firstPass && selectedOptions.rework) {
                    const firstPassValue = parseFloat((parseDuration(time.first_pass).totalDays || 0).toFixed(1));
                    const reworkValue = parseFloat((parseDuration(time.rework).totalDays || 0).toFixed(1));
                    value = firstPassValue + reworkValue;
                }
    
                return acc + value;
            }, 0);
    
            return {
                ...item,
                ...executionTimeData,
                lead_time: formatValue(leadTime), 
                isRework: item.execution_time.some((t) => parseDuration(t.rework).totalDays > 0), 
            };
        });
    }, [releaseItem,selectedOptions]);

    const defaultColDef = useMemo(() => ({
        resizable: true,
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 120, 
        cellClass: 'border-right',
    }), []);

    const onGridReady = useCallback((params: GridReadyEvent) => {
        gridApiRef.current = params.api;
    
        if (params.api && rowData.length) {
            params.api.sizeColumnsToFit();
        }
    }, [rowData]);
    

    useEffect(() => {
        if (gridRef.current && gridRef.current.api) {
          gridRef.current.api.sizeColumnsToFit();
        }
      }, [rowData]);
      
    const formatDate = (date: string) => {
        const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
        return new Date(date).toLocaleDateString('en-GB', options);
    };

    const modalTitle = releaseItem
    ? releaseItem.date
      ? `Release ${releaseItem.release} deployed on ${formatDate(releaseItem.date)}`
      : releaseItem.release
    : "";

    if (!releaseItem || !releaseItem.work_items) return null;

    return (
        <ModalDialog
            title={modalTitle}
            visible={true}
            onCancel={onClose}
            footer={null}
            modalWidth="1100px"
            styles={{
                  height: "400px",
                  padding: 5,
              }}
            resizable={true}

        >
             <div style={containerStyle}>
            <div style={{ height: '100%', width: '100%' }} className={`ag-theme-alpine grid_style mt-23`}>
                <TsGrid
                    rowData={rowData}
                    columnDefs={columnDefs}
                    onGridReady={onGridReady}
                    gridRef={gridRef}
                    gridStyle={gridStyle}
                    defaultColDef={defaultColDef}
                />
            </div>
            </div>
        </ModalDialog>
    );
};

export default FullDetailsModal;